import { HttpClient, HttpEventType, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { RestApiService } from './rest-api.service';
import { environment } from 'src/environments/environment';
import {
  DownloadSharedFileParams,
  FileSharingDetails,
  FileSharingResponse,
  GetFileSharing,
  SendFilesToShareParams,
} from 'src/app/types/file-sharing.type';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { Response, UploadProgress } from 'src/app/types';
import saveAs from 'file-saver';

const CUSTOMER_PATH = '/customer';
const FILE_SHARING_PATH = CUSTOMER_PATH + '/file-sharing';

@Injectable({
  providedIn: 'root',
})
export class FileSharingApiService {
  private restApiService = inject(RestApiService);
  private http = inject(HttpClient);

  getSharedFiles(
    params: GetFileSharing,
  ): Observable<Response<FileSharingResponse>> {
    return this.restApiService.post<FileSharingResponse>(
      environment.api_url + FILE_SHARING_PATH,
      params,
    );
  }

  createDraft(): Observable<Response<number>> {
    return this.restApiService.post<number>(
      environment.api_url + FILE_SHARING_PATH + '/create-draft',
      {},
    );
  }

  sendFilesToShare(params: SendFilesToShareParams): Observable<Response<void>> {
    return this.restApiService.post<void>(
      environment.api_url + FILE_SHARING_PATH + '/send',
      params,
    );
  }

  uploadFileToShare(file: File, id: string): Observable<UploadProgress> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('id', id);

    return this.http
      .post(
        `${environment.api_url}${FILE_SHARING_PATH}/${id}/upload`,
        formData,
        {
          reportProgress: true,
          observe: 'events',
        },
      )
      .pipe(
        map((event) => {
          if (event.type === HttpEventType.UploadProgress) {
            const progress = Math.round(
              100 * (event.loaded / (event.total || 1)),
            );
            return { progress };
          } else if (event.type === HttpEventType.Response) {
            return {
              fileKey: (event.body as Response).result || '',
              progress: (event.body as Response).ok ? 100 : 0,
              error: (event.body as Response).ok
                ? undefined
                : (event.body as Response).errors![0].message,
            };
          }

          return { progress: 0 };
        }),
      );
  }

  getFileSharingDetails(id: number): Observable<Response<FileSharingDetails>> {
    return this.restApiService.get<FileSharingDetails>(
      environment.api_url + FILE_SHARING_PATH + `/${id}`,
    );
  }

  downloadSharedFile(
    contentName: string,
    params: DownloadSharedFileParams,
  ): Observable<boolean> {
    return this.http
      .post(`${environment.api_url}${FILE_SHARING_PATH}/download`, params, {
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        switchMap((response: HttpResponse<Blob>) => {
          if (response.body === null) {
            console.error('Received null response body');
            return of(false);
          }

          const contentType =
            response.headers.get('Content-Type') || 'application/octet-stream';
          const blob = new Blob([response.body], { type: contentType });

          saveAs(blob, contentName);
          return of(true);
        }),
        catchError((error) => {
          console.error('Ошибка загрузки:', error);

          return of(false);
        }),
      );
  }

  deleteSharedFile(params: DownloadSharedFileParams): Observable<void> {
    return this.http.delete<void>(environment.api_url + FILE_SHARING_PATH, {
      body: params,
    });
  }
}
